/**
 * Создание бренда (§7): имя, канал(ы), тир, категории.
 *
 * Модалка, а не панель: пока бренда нет, показывать нечего, а набор полей
 * фиксирован. Созданный бренд живёт в черновике до подтверждения и в таблице
 * всегда виден — даже если не подходит под активный фильтр (П1, §4.3).
 *
 * Уникальность имени проверяет сервер (`assertNameFree`) и присылает готовый
 * текст ошибки. Срез 295 брендов уже в памяти, поэтому очевидный случай
 * ловится до запроса — и тогда рядом стоит ссылка «Открыть?» (§12.7).
 *
 * Кнопку «+ Бренд» в строке управления подключает главный экран: диалог
 * экспортируется отдельно и ничего не знает о том, кто его открыл.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { Brand, BrandChannelPatch, Category, ChannelCode } from '@shared/types.ts'
import {
  Button, Checkbox, ConsequenceLine, FieldRow, IconPlus, Input, Modal,
} from '../../../components/index.js'
import { useApp } from '../../../store/app-store.js'
import { CategoryPicker } from './CategoriesBlock.js'
import { TierPicker } from './ChannelBlock.js'
import { cardTexts } from './texts.card.js'
import './AddBrandDialog.css'

export interface AddBrandDialogProps {
  open: boolean
  onClose: () => void
  /** Созданный бренд: главный экран может поставить на него курсор. */
  onCreated?: (brand: Brand) => void
  /** Открыть карточку созданного бренда (по умолчанию — да). */
  openCardAfter?: boolean
}

export function AddBrandDialog({ open, onClose, onCreated, openCardAfter = true }: AddBrandDialogProps) {
  const navigate = useNavigate()
  const bootstrap = useApp((state) => state.bootstrap)
  const brands = useApp((state) => state.brands)
  const createBrand = useApp((state) => state.createBrand)
  const toast = useApp((state) => state.toast)

  const channels = bootstrap?.channels ?? []
  const user = bootstrap?.user ?? null
  const canCreate = Boolean(user?.permissions.includes('brands.create'))
  const tiers = (bootstrap?.enums.tier ?? []).filter((item) => item.isActive).map((item) => item.value)

  const [name, setName] = useState('')
  const [picked, setPicked] = useState<Set<ChannelCode>>(new Set<ChannelCode>(['retail']))
  /* Тир — свой у каждого канала: в Рознице бренд может быть A+, а в Опте C.
     Один общий тир на оба канала врал бы в половине случаев. */
  const [tiers_, setTiers] = useState<Partial<Record<ChannelCode, string | null>>>({})
  const [categories, setCategories] = useState<Category[]>([])
  const [pickerOpen, setPickerOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [taken, setTaken] = useState<Brand | null>(null)
  const [busy, setBusy] = useState(false)
  const nameRef = useRef<HTMLInputElement | null>(null)
  /* Якорь поповера держим в состоянии, а не в ref: чтение ref.current во
     время отрисовки на первом проходе даёт null, перерисовки это не вызывает,
     и поповер открывался без якоря — в левом нижнем углу экрана. */
  const [addCategoryNode, setAddCategoryNode] = useState<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return
    setName('')
    setPicked(new Set<ChannelCode>([bootstrap?.settings.defaultChannel ?? 'retail']))
    setTiers({})
    setCategories([])
    setError(null)
    setTaken(null)
    setBusy(false)
  }, [open, bootstrap?.settings.defaultChannel])

  const trimmed = name.trim()

  /** Срез уже в памяти: одноимённый бренд находим до запроса (§12.7). */
  const localClash = useMemo(() => {
    if (trimmed === '') return null
    const needle = trimmed.toLowerCase()
    return brands.find((item) => item.name.toLowerCase() === needle) ?? null
  }, [brands, trimmed])

  async function submit(): Promise<void> {
    if (busy) return
    if (trimmed === '') {
      setError(cardTexts.create.nameRequired)
      nameRef.current?.focus()
      return
    }
    if (picked.size === 0) {
      setError(cardTexts.create.channelsRequired)
      return
    }
    if (localClash) {
      setTaken(localClash)
      setError(cardTexts.create.nameTakenLocal(localClash.name))
      return
    }

    const missingTier = [...picked].filter((code) => !tiers_[code])
    if (missingTier.length > 0) {
      setError(cardTexts.create.tierRequired)
      return
    }

    const channelInput: Partial<Record<ChannelCode, BrandChannelPatch>> = {}
    for (const code of picked) {
      const patch: BrandChannelPatch = { isPresent: true }
      const value = tiers_[code]
      if (value) patch.tier = value
      channelInput[code] = patch
    }

    setBusy(true)
    setError(null)
    setTaken(null)
    /* Стор гасит ошибку мутации тостом и возвращает null (см. отчёт модуля):
       текст отказа достаём из того же тоста и показываем у поля. */
    const toastsBefore = useApp.getState().toasts.length
    const created = await createBrand({
      name: trimmed,
      channels: channelInput,
      categoryIds: categories.map((item) => item.id),
    })
    setBusy(false)

    if (!created) {
      const fresh = useApp.getState().toasts.slice(toastsBefore)
      const failure = fresh.reverse().find((item) => item.kind === 'error')
      setError(failure?.text ?? cardTexts.create.failed)
      nameRef.current?.focus()
      return
    }

    toast('success', cardTexts.create.created(created.name))
    onCreated?.(created)
    onClose()
    if (openCardAfter) navigate(`/brands/${created.slug}`)
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={cardTexts.create.title}
      size="m"
      footerNote={cardTexts.create.footerNote}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {cardTexts.create.cancel}
          </Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!canCreate || trimmed === '' || picked.size === 0
              || [...picked].some((code) => !tiers_[code])}
            onClick={() => void submit()}
          >
            {cardTexts.create.submit}
          </Button>
        </>
      }
    >
      {!canCreate ? <ConsequenceLine tone="danger">{cardTexts.create.denied}</ConsequenceLine> : null}

      <FieldRow label={cardTexts.create.name} htmlFor="add-brand-name">
        <Input
          ref={nameRef}
          id="add-brand-name"
          value={name}
          autoFocus
          maxLength={120}
          disabled={!canCreate}
          placeholder={cardTexts.create.namePlaceholder}
          error={error ?? undefined}
          onChange={(event) => {
            setName(event.target.value)
            setError(null)
            setTaken(null)
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void submit()
            }
          }}
        />
      </FieldRow>

      {taken ? (
        <ConsequenceLine>
          <button
            type="button"
            className="add-brand__open"
            onClick={() => {
              onClose()
              navigate(`/brands/${taken.slug}`)
            }}
          >
            {cardTexts.create.nameTakenOpen}
          </button>
        </ConsequenceLine>
      ) : null}

      <FieldRow label={cardTexts.create.channels} group consequence={<ConsequenceLine>{cardTexts.create.channelsHint}</ConsequenceLine>}>
        <div className="add-brand__channels">
          {channels.map((item) => (
            <Checkbox
              key={item.code}
              checked={picked.has(item.code)}
              disabled={!canCreate}
              label={item.name}
              onChange={(checked) => {
                const next = new Set(picked)
                if (checked) next.add(item.code)
                else next.delete(item.code)
                setPicked(next)
                setError(null)
              }}
            />
          ))}
        </div>
      </FieldRow>

      {/* Тир спрашиваем отдельно по каждому выбранному каналу: это разные
          решения, и пустой тир в таблице читается как недоделанный бренд. */}
      {[...picked].map((code) => {
        const name = channels.find((item) => item.code === code)?.name ?? code
        return (
          <FieldRow
            key={code}
            label={cardTexts.create.tierIn(name)}
            group
            consequence={<ConsequenceLine>{cardTexts.create.tierHint}</ConsequenceLine>}
          >
            <TierPicker
              value={tiers_[code] ?? null}
              options={tiers}
              label={cardTexts.create.tierIn(name)}
              disabled={!canCreate}
              onChange={(value) => {
                setTiers((previous) => ({ ...previous, [code]: value }))
                setError(null)
              }}
            />
          </FieldRow>
        )
      })}

      <FieldRow label={cardTexts.create.categories} group consequence={<ConsequenceLine>{cardTexts.create.categoriesHint}</ConsequenceLine>}>
        <div className="add-brand__cats">
          {categories.map((category) => (
            <Checkbox
              key={category.id}
              checked
              label={category.name}
              onChange={() => setCategories(categories.filter((item) => item.id !== category.id))}
            />
          ))}
          <Button
            ref={setAddCategoryNode}
            variant="ghost"
            size="m"
            iconBefore={<IconPlus size={16} />}
            disabled={!canCreate}
            aria-expanded={pickerOpen}
            aria-haspopup="dialog"
            onClick={() => setPickerOpen((previous) => !previous)}
          >
            {cardTexts.categories.add}
          </Button>
        </div>
      </FieldRow>

      <CategoryPicker
        open={pickerOpen}
        anchor={addCategoryNode}
        onClose={() => setPickerOpen(false)}
        attachedIds={new Set<number>()}
        multiple
        selectedIds={new Set(categories.map((item) => item.id))}
        onPick={(category) => {
          setCategories((previous) =>
            previous.some((item) => item.id === category.id)
              ? previous.filter((item) => item.id !== category.id)
              : [...previous, category],
          )
        }}
        onRequest={() => {
          /* Нового бренда ещё нет — запрос категории уходит из его карточки. */
          toast('info', cardTexts.categories.freeInputDenied)
          setPickerOpen(false)
        }}
      />
    </Modal>
  )
}
